import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useOutletContext, useParams, useNavigate, useLocation } from 'react-router-dom'
import { useI18n } from 'gunsi-auth'
import { useTally, useRangeTransactions } from '../hooks/useTally.jsx'
import { useTallyI18n } from '../i18n/tally.js'
import { formatAmount, formatDate, formatTime, expandAllSlices, wallClockKey } from '../format.js'
import { Emoji } from '../components/Emoji.jsx'
import { CategoryIcon } from '../components/CategoryIcon.jsx'
import { MonthPicker } from '../components/MonthPicker.jsx'
import {
  shiftLilMonth, lilMonthRange, formatLilMonthJsx, formatLilYearJsx,
  lilToHe, heToLil,
} from '../lil.jsx'
import { pscObjectToString, pscToGregorian } from '@justhyur/lil-calendar'
import { loadTransactionsByRange } from '../api.js'
import { TransactionModal } from './Transactions.jsx'

function shiftYearMonth(ym, delta) {
  const [y, m] = ym.split('-').map(Number)
  const d = new Date(y, m - 1 + delta, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}
function formatYearMonth(ym, locale) {
  const [y, m] = ym.split('-').map(Number)
  const lang = (locale || 'en').startsWith('it') ? 'it' : 'en'
  return new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric' })
    .format(new Date(y, m - 1, 1))
}
function monthShortLabel(ym, locale) {
  const [y, m] = ym.split('-').map(Number)
  const lang = (locale || 'en').startsWith('it') ? 'it' : 'en'
  return new Intl.DateTimeFormat(lang, { month: 'short' }).format(new Date(y, m - 1, 1))
}

// Greg-day range [from, to) for a "YYYY-MM" string.
function gregRange(ym) {
  const [y, m] = ym.split('-').map(Number)
  const pad = (n) => String(n).padStart(2, '0')
  const ny = m === 12 ? y + 1 : y
  const nm = m === 12 ? 1 : m + 1
  return { from: `${y}-${pad(m)}-01`, to: `${ny}-${pad(nm)}-01` }
}

// Descendant ids of a category (including the category itself).
function subtreeIds(categories, rootId) {
  const ids = new Set([rootId])
  let added = true
  while (added) {
    added = false
    for (const c of categories) {
      if (c.parentId && ids.has(c.parentId) && !ids.has(c.id)) {
        ids.add(c.id); added = true
      }
    }
  }
  return ids
}

export function CategoryDetail() {
  const t = useTallyI18n()
  const { locale } = useI18n()
  const { subTopbar } = useOutletContext()
  const {
    data, addTransaction, updateTransaction, removeTransaction, clearTxCache,
    viewedGreg: viewedMonth, setViewedGreg: setViewedMonth,
    viewedLil, setViewedLil,
  } = useTally()
  const navigate = useNavigate()
  const params = useParams()
  const location = useLocation()

  const isLil   = data.calendar === 'lil'
  const yearEra = data.yearEra ?? 'lil'
  const categoryId = params.categoryId
  const category = useMemo(
    () => data.categories.find(c => c.id === categoryId),
    [data.categories, categoryId],
  )

  // ── URL → state sync (year/month from params) ────────────────────────
  useEffect(() => {
    if (!params.year || !params.month) return
    const y = Number(params.year)
    if (!Number.isFinite(y)) return
    if (isLil) {
      const lilYear = yearEra === 'he' ? heToLil(y) : y
      if (params.month === 'S' || params.month === 's') {
        setViewedLil({ year: lilYear, special: true })
      } else {
        const m = Number(params.month)
        if (Number.isFinite(m) && m >= 1 && m <= 13) {
          setViewedLil({ year: lilYear, month: m })
        }
      }
    } else {
      const m = Number(params.month)
      if (Number.isFinite(m) && m >= 1 && m <= 12) {
        setViewedMonth(`${y}-${String(m).padStart(2, '0')}`)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.year, params.month, isLil, yearEra])

  // ── state → URL ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!categoryId) return
    let yr, mo
    if (isLil) {
      yr = yearEra === 'he' ? lilToHe(viewedLil.year) : viewedLil.year
      mo = viewedLil.special ? 'S' : viewedLil.month
    } else {
      const [y, m] = viewedMonth.split('-').map(Number)
      yr = y; mo = m
    }
    const path = `/stats/${yr}/${mo}/cat/${categoryId}`
    if (window.location.pathname !== path) {
      navigate(path, { replace: true })
    }
  }, [isLil, yearEra, viewedMonth, viewedLil, categoryId, navigate])

  // ── Period (monthly / yearly) ────────────────────────────────────────
  // Open matching the period we were drilled from (Stats passes it via nav
  // state), so coming from a yearly view doesn't snap back to monthly.
  const [period, setPeriod] = useState(
    location.state?.period === 'yearly' ? 'yearly' : 'monthly'
  )

  // ── Current period range ─────────────────────────────────────────────
  const { range, cacheKey, periodLabel } = useMemo(() => {
    if (period === 'yearly') {
      if (isLil) {
        const y = viewedLil.year
        const from = pscToGregorian(pscObjectToString({ year: y, month: 1, day: 1 }))
        const to   = pscToGregorian(pscObjectToString({ year: y + 1, month: 1, day: 1 }))
        return {
          range: { from, to },
          cacheKey: `lilY:${y}`,
          periodLabel: formatLilYearJsx(y, yearEra),
        }
      }
      const y = Number(viewedMonth.split('-')[0])
      return {
        range: { from: `${y}-01-01`, to: `${y + 1}-01-01` },
        cacheKey: `gregY:${y}`,
        periodLabel: String(y),
      }
    }
    if (isLil) {
      const r = lilMonthRange(viewedLil)
      return {
        range: r,
        cacheKey: `lil:${viewedLil.year}-${viewedLil.special ? 'S' : viewedLil.month}`,
        periodLabel: viewedLil.special
          ? <>{formatLilYearJsx(viewedLil.year, yearEra)} – {formatLilYearJsx(viewedLil.year + 1, yearEra)}</>
          : formatLilMonthJsx(viewedLil, yearEra),
      }
    }
    const r = gregRange(viewedMonth)
    return {
      range: r,
      cacheKey: `greg:${viewedMonth}`,
      periodLabel: formatYearMonth(viewedMonth, locale),
    }
  }, [period, isLil, viewedMonth, viewedLil, yearEra, locale])

  const { transactions: monthTx, loading } = useRangeTransactions(cacheKey, range.from, range.to)

  // ── Filter tx in this category subtree ──────────────────────────────
  const subtree = useMemo(
    () => category ? subtreeIds(data.categories, category.id) : new Set(),
    [data.categories, category],
  )
  // Expand split tx into per-slice rows, then keep only the slices that
  // - belong to this category subtree, AND
  // - actually fall inside the viewed [range.from, range.to) window
  //   (a parent doc can be returned by the backend's overlap query for any
  //   bucket it touches; we want the slice that matches the current view).
  // Re-sort by displayDate desc — expansion interleaves split siblings with
  // unrelated tx, so the backend's parent-only sort doesn't survive.
  const categoryTx = useMemo(() => {
    const flat = expandAllSlices(monthTx)
    const filtered = flat.filter(tx => {
      if (!tx.categoryId || !subtree.has(tx.categoryId)) return false
      const d = (tx.effectiveDate || tx.date || '').slice(0, 10)
      return d >= range.from && d < range.to
    })
    filtered.sort((a, b) =>
      wallClockKey(b.effectiveDate || b.date)
        .localeCompare(wallClockKey(a.effectiveDate || a.date))
    )
    return filtered
  }, [monthTx, subtree, range.from, range.to])

  const balance = useMemo(
    () => categoryTx.reduce((s, tx) => s + (tx.amount_base ?? tx.amount ?? 0), 0),
    [categoryTx],
  )

  // ── Breakdown ───────────────────────────────────────────────────────
  // If this category has direct children, show "All 100%" + each child's sub-total.
  // If leaf, aggregate by tx.name (grouping unnamed as "—").
  const directChildren = useMemo(
    () => (category ? data.categories.filter(c => c.parentId === category.id) : []),
    [data.categories, category],
  )
  const childTotals = useMemo(() => {
    if (directChildren.length === 0) return []
    const flat = expandAllSlices(monthTx)
    return directChildren.map(child => {
      const childSubtree = subtreeIds(data.categories, child.id)
      const sum = flat
        .filter(tx => {
          if (!tx.categoryId || !childSubtree.has(tx.categoryId)) return false
          const d = (tx.effectiveDate || tx.date || '').slice(0, 10)
          return d >= range.from && d < range.to
        })
        .reduce((s, tx) => s + (tx.amount_base ?? tx.amount ?? 0), 0)
      return { id: child.id, name: child.name, icon: child.icon, total: sum }
    }).filter(r => r.total > 0).sort((a, b) => b.total - a.total)
  }, [directChildren, data.categories, monthTx, range.from, range.to])

  const nameBuckets = useMemo(() => {
    if (directChildren.length > 0) return [] // not needed for parent
    const m = new Map()
    for (const tx of categoryTx) {
      const key = tx.name?.trim() || '—'
      m.set(key, (m.get(key) ?? 0) + (tx.amount_base ?? tx.amount ?? 0))
    }
    return [...m.entries()].map(([name, total]) => ({ name, total }))
      .filter(r => r.total > 0)
      .sort((a, b) => b.total - a.total)
  }, [categoryTx, directChildren.length])

  // ── 12-month trend ─────────────────────────────────────────────────
  // Fetches one big range covering 12 trailing periods, groups client-side.
  const trendBuckets = useMemo(() => {
    const buckets = []
    if (isLil) {
      let v = viewedLil
      for (let i = 0; i < 12; i++) {
        const r = lilMonthRange(v)
        buckets.unshift({
          key: `lil:${v.year}-${v.special ? 'S' : v.month}`,
          from: r.from, to: r.to,
          label: v.special ? 'S' : romanShort(v.month),
          isCurrent: i === 0,
        })
        v = shiftLilMonth(v, -1)
      }
    } else {
      let ym = viewedMonth
      for (let i = 0; i < 12; i++) {
        const r = gregRange(ym)
        buckets.unshift({
          key: `greg:${ym}`,
          from: r.from, to: r.to,
          label: monthShortLabel(ym, locale),
          isCurrent: i === 0,
        })
        ym = shiftYearMonth(ym, -1)
      }
    }
    return buckets
  }, [isLil, viewedMonth, viewedLil, locale])

  const [trendSums, setTrendSums] = useState({})  // { [bucketKey]: amount }
  useEffect(() => {
    if (!category) return
    let alive = true
    const overallFrom = trendBuckets[0]?.from
    const overallTo   = trendBuckets[trendBuckets.length - 1]?.to
    if (!overallFrom || !overallTo) return
    ;(async () => {
      try {
        const txs = await loadTransactionsByRange(overallFrom, overallTo)
        if (!alive) return
        const sums = {}
        for (const b of trendBuckets) sums[b.key] = 0
        // Expand split tx into per-slice rows so a 600€ split-by-4 contributes
        // its slice amounts to each bucket they fall into (not the full 600
        // to the parent's bucket).
        const flat = expandAllSlices(txs)
        for (const tx of flat) {
          if (!tx.categoryId || !subtree.has(tx.categoryId)) continue
          const d = (tx.effectiveDate || tx.date || '').slice(0, 10)
          const b = trendBuckets.find(bk => d >= bk.from && d < bk.to)
          if (b) sums[b.key] += (tx.amount_base ?? tx.amount ?? 0)
        }
        if (alive) setTrendSums(sums)
      } catch (e) {
        console.error('[detail] trend fetch failed', e)
      }
    })()
    return () => { alive = false }
  }, [category, subtree, trendBuckets])

  // ── Month nav ──────────────────────────────────────────────────────
  function shiftViewed(delta) {
    if (period === 'yearly') {
      if (isLil) setViewedLil(v => ({ year: v.year + delta, month: v.month ?? 1 }))
      else {
        const [y, m] = viewedMonth.split('-').map(Number)
        setViewedMonth(`${y + delta}-${String(m).padStart(2, '0')}`)
      }
    } else {
      if (isLil) setViewedLil(v => shiftLilMonth(v, delta))
      else       setViewedMonth(v => shiftYearMonth(v, delta))
    }
  }
  const [pickerOpen, setPickerOpen] = useState(false)
  function jumpToMonth(target) {
    if (isLil) setViewedLil(target)
    else       setViewedMonth(target)
  }

  // ── Tx list + edit modal ───────────────────────────────────────────
  const [editing, setEdit] = useState(null)

  // ── Header (portaled into sub-topbar) ──────────────────────────────
  // Mirrors the Stats/Transactions two-row sticky pattern:
  //   Row 1: back + icon + category title
  //   Row 2: month nav + period select
  const stickyContent = (
    <div className="cat-sticky-head">
      <div className="cat-title-bar">
        <button type="button" className="cat-back" onClick={() => navigate(-1)} aria-label="Back">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
        </button>
        <span className="cat-title-icon">
          <CategoryIcon category={category} size="1.4rem" fallback="💰" />
        </span>
        <span className="cat-title-name">{category?.name ?? '—'}</span>
      </div>
      <header className="view-header view-header-month">
        <div className="month-nav">
          <button className="month-nav-arrow" onClick={() => shiftViewed(-1)} aria-label="Previous">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <button
            type="button"
            className={`month-nav-label${period === 'monthly' && isLil && viewedLil.special ? ' is-range' : ''}`}
            onClick={() => period === 'monthly' && setPickerOpen(true)}
          >
            {periodLabel}
          </button>
          <button className="month-nav-arrow" onClick={() => shiftViewed(+1)} aria-label="Next">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </div>
        <select
          className="period-select"
          value={period}
          onChange={e => setPeriod(e.target.value)}
        >
          <option value="monthly">{t.stats.monthly}</option>
          <option value="yearly">{t.stats.yearly}</option>
        </select>
      </header>
    </div>
  )

  if (!category) {
    return (
      <>
        {subTopbar && createPortal(stickyContent, subTopbar)}
        <div className="view view-cat-detail">
          <div className="view-empty"><span>—</span></div>
        </div>
      </>
    )
  }

  const valCls = category.type === 'income' ? 'amount-income' : 'amount-expense'
  const cur = data.currency
  const hasChildren = directChildren.length > 0

  return (
    <>
      {subTopbar && createPortal(stickyContent, subTopbar)}
      <MonthPicker
        open={pickerOpen && period === 'monthly'}
        onClose={() => setPickerOpen(false)}
        mode={isLil ? 'lil' : 'greg'}
        viewed={isLil ? viewedLil : viewedMonth}
        onSelect={jumpToMonth}
        yearEra={yearEra}
        locale={locale}
        t={t}
      />
      <div className="view view-cat-detail">
        <div className="cat-balance">
          <span className="cat-balance-label">{t.dashboard.net}</span>
          <span className={`cat-balance-val ${valCls}`}>{formatAmount(balance, cur)}</span>
        </div>

        {/* Breakdown */}
        {loading ? (
          <div className="view-empty"><div className="loading-spinner" /></div>
        ) : (
          <div className="cat-breakdown">
            <div className="stats-row cat-row-all">
              <span className="stats-pct">100%</span>
              <span className="cat-icon" />
              <span className="stats-name">{t.detail.all}</span>
              <span className={`stats-val ${valCls}`}>{formatAmount(balance, cur)}</span>
            </div>
            {hasChildren
              ? childTotals.map(c => {
                  const pct = balance > 0 ? (c.total / balance) * 100 : 0
                  return (
                    <div
                      key={c.id}
                      className="stats-row clickable"
                      onClick={() => navigate(`/stats/${params.year}/${params.month}/cat/${c.id}`)}
                      role="button" tabIndex={0}
                    >
                      <span className="stats-pct">{pct >= 10 ? Math.round(pct) : pct.toFixed(1)}%</span>
                      <span className="cat-icon"><Emoji symbol={c.icon} size="1.3rem" /></span>
                      <span className="stats-name">{c.name}</span>
                      <span className={`stats-val ${valCls}`}>{formatAmount(c.total, cur)}</span>
                    </div>
                  )
                })
              : nameBuckets.map((n, i) => {
                  const pct = balance > 0 ? (n.total / balance) * 100 : 0
                  return (
                    <div key={i} className="stats-row">
                      <span className="stats-pct">{pct >= 10 ? Math.round(pct) : pct.toFixed(1)}%</span>
                      <span className="cat-icon" />
                      <span className="stats-name">{n.name}</span>
                      <span className={`stats-val ${valCls}`}>{formatAmount(n.total, cur)}</span>
                    </div>
                  )
                })}
          </div>
        )}

        {/* Trend chart */}
        <TrendChart buckets={trendBuckets} sums={trendSums} cur={cur} type={category.type} />

        {/* Tx list (this period) */}
        <div className="cat-tx-list">
          <h3 className="cat-tx-head">{t.transactions.title}</h3>
          {categoryTx.length === 0 ? (
            <div className="view-empty"><span>{t.transactions.empty}</span></div>
          ) : (
            <div className="list">
              {categoryTx.map(tx => {
                const txCat = data.categories.find(c => c.id === tx.categoryId)
                const cls = tx.type === 'income' ? 'amount-income' : 'amount-expense'
                const sign = tx.type === 'income' ? '+' : ''
                const sliceBadge = tx._sliceIdx != null
                  ? t.transactions.splitBadge
                      .replace('{idx}', tx._sliceIdx + 1)
                      .replace('{total}', tx._sliceTotal)
                  : null
                return (
                  <div
                    key={tx.id}
                    className={`list-row clickable${sliceBadge ? ' is-slice' : ''}`}
                    onClick={() => {
                      // Slice → resolve back to the parent in the cached
                      // monthTx so the modal edits the source, not a virtual.
                      if (tx._parentId) {
                        const parent = monthTx.find(t => t.id === tx._parentId)
                        setEdit(parent ?? tx)
                      } else {
                        setEdit(tx)
                      }
                    }}
                    role="button" tabIndex={0}
                  >
                    <div className="list-row-left">
                      <span className="cat-icon">
                        <Emoji symbol={txCat?.icon ?? (tx.type === 'income' ? '💰' : '💸')} size="1.4rem" />
                      </span>
                      <div>
                        <div className="list-row-name">
                          {tx.name || txCat?.name || t.transactions.types[tx.type]}
                          {sliceBadge && (
                            <span className="tx-slice-badge">{sliceBadge}</span>
                          )}
                        </div>
                        <div className="list-row-meta">
                          {/* Flat list (no day headers), so the date must sit next to the time */}
                          {[`${formatDate(tx.effectiveDate || tx.date, locale, data.dateFormat)} ${formatTime(tx.effectiveDate || tx.date, locale)}`,
                            tx.name && txCat ? txCat.name : null, tx.note]
                            .filter(Boolean).join(' · ')}
                        </div>
                      </div>
                    </div>
                    <div className="list-row-right">
                      <span className={cls}>
                        {sign}{formatAmount(tx.amount_base ?? tx.amount, cur)}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {editing && (
        <TransactionModal
          key={editing.id ?? (editing._copyTag ?? 'new')}
          t={t}
          existing={editing}
          categories={data.categories}
          defaultCurrency={data.currency}
          isLilAvailable={isLil}
          yearEra={yearEra}
          locale={locale}
          onClose={() => setEdit(null)}
          onSubmit={(tx) => {
            if (editing.id) updateTransaction(editing.id, tx)
            else addTransaction(tx)
            setEdit(null)
          }}
          onDelete={() => {
            if (editing.id) { removeTransaction(editing.id); setEdit(null) }
          }}
          onCopy={(form) => {
            const next = { _copyTag: Date.now() }
            if (form.type) next.type = form.type
            if (form.amount !== undefined) {
              const v = parseFloat(form.amount)
              if (Number.isFinite(v)) next.amount = v
            }
            if (form.currency) next.currency = form.currency
            if (form.date || form.time) {
              const dd = new Date()
              const p = (x) => String(x).padStart(2, '0')
              const d = form.date || `${dd.getFullYear()}-${p(dd.getMonth() + 1)}-${p(dd.getDate())}`
              const tm = form.time || `${p(dd.getHours())}:${p(dd.getMinutes())}`
              next.date = `${d}T${tm}` // naive wall-clock, no TZ conversion
            }
            if (form.categoryId) next.categoryId = form.categoryId
            if (form.name)       next.name       = form.name
            if (form.note)       next.note       = form.note
            setEdit(next)
          }}
        />
      )}
    </>
  )
}

function romanShort(n) {
  const R = ['','I','II','III','IV','V','VI','VII','VIII','IX','X','XI','XII','XIII']
  return R[n] ?? String(n)
}

// ── Native-SVG line chart for the 12-period trend ────────────────────
function TrendChart({ buckets, sums, cur, type }) {
  const W = 600, H = 200
  const PAD_L = 36, PAD_R = 12, PAD_T = 16, PAD_B = 28
  const innerW = W - PAD_L - PAD_R
  const innerH = H - PAD_T - PAD_B

  const values = buckets.map(b => sums[b.key] ?? 0)
  const max = Math.max(1, ...values)
  const niceMax = niceCeil(max)
  const stepX = buckets.length > 1 ? innerW / (buckets.length - 1) : 0

  const points = buckets.map((b, i) => ({
    x: PAD_L + i * stepX,
    y: PAD_T + innerH - (values[i] / niceMax) * innerH,
    v: values[i],
    label: b.label,
    isCurrent: b.isCurrent,
  }))

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ')
  const strokeColor = type === 'income' ? 'var(--g-income)' : 'var(--g-expense)'

  // Y-axis ticks: 0, half, max
  const ticks = [0, niceMax / 2, niceMax]

  return (
    <div className="cat-trend">
      <svg viewBox={`0 0 ${W} ${H}`} className="cat-trend-svg" preserveAspectRatio="xMidYMid meet">
        {/* Grid lines + Y labels */}
        {ticks.map((t, i) => {
          const y = PAD_T + innerH - (t / niceMax) * innerH
          return (
            <g key={i}>
              <line x1={PAD_L} x2={W - PAD_R} y1={y} y2={y}
                stroke="var(--g-border)" strokeWidth="1"
                strokeDasharray={i === 0 ? null : '2 3'} />
              <text x={PAD_L - 6} y={y + 3} textAnchor="end" fontSize="9" fill="var(--g-text-muted)">
                {compactNumber(t)}
              </text>
            </g>
          )
        })}
        {/* Line */}
        <path d={linePath} fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {/* Dots */}
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={p.isCurrent ? 4 : 3}
            fill={strokeColor}
            stroke={p.isCurrent ? 'var(--g-bg)' : 'none'}
            strokeWidth={p.isCurrent ? 2 : 0}>
            <title>{`${p.label}: ${formatAmount(p.v, cur)}`}</title>
          </circle>
        ))}
        {/* X labels */}
        {points.map((p, i) => (
          <text key={i} x={p.x} y={H - 8} textAnchor="middle" fontSize="10"
            fill={p.isCurrent ? 'var(--g-text)' : 'var(--g-text-muted)'}
            fontWeight={p.isCurrent ? 700 : 400}>
            {p.label}
          </text>
        ))}
      </svg>
    </div>
  )
}

// 1k → "1k", 12500 → "12.5k", etc.
function compactNumber(n) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`
  return String(Math.round(n))
}

function niceCeil(n) {
  if (n <= 0) return 1
  const mag = Math.pow(10, Math.floor(Math.log10(n)))
  const norm = n / mag
  if (norm <= 1) return 1 * mag
  if (norm <= 2) return 2 * mag
  if (norm <= 5) return 5 * mag
  return 10 * mag
}
